import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

import { CalendarPopover } from "./CalendarPopover";
import { DateInput } from "./DateInput";
import { useCalendarPosition } from "./usePosition";
import type { DatePickerProps } from "./types";

/** Single-date picker — same primitive as DateRangePicker, one month visible. */
export function DatePicker({
  value,
  onChange,
  disabled,
  className,
  id,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const firstNavRef = useRef<HTMLButtonElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const calPos = useCalendarPosition(wrapRef, open, popoverRef);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inAnchor = wrapRef.current?.contains(target);
      const inPopover = popoverRef.current?.contains(target);
      if (!inAnchor && !inPopover) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <div ref={wrapRef} className={cn("relative inline-block", className)}>
      <DateInput
        id={id}
        label={ariaLabel ?? "Date"}
        value={value}
        inputRef={inputRef}
        active={focused}
        onCommit={(d) => {
          onChange(d);
          if (d && !disabled) setOpen(true);
        }}
        onFocus={() => {
          if (disabled) return;
          setFocused(true);
          setOpen(true);
        }}
        onTab={() => firstNavRef.current?.focus()}
      />
      {open && !disabled &&
        // Portal to body so the calendar escapes any sibling sticky
        // stacking context — same fix pattern as the DS Tooltip.
        createPortal(
          <div
            ref={popoverRef}
            // pointer-events-auto: a modal Radix Dialog sets `pointer-events:
            // none` on <body>, which this body-portaled popover would otherwise
            // inherit (making the calendar unclickable). Stopping pointerdown
            // keeps the Dialog's DismissableLayer from reading a day click as an
            // outside interaction and closing the whole dialog.
            className="fixed z-50 animate-scale-in pointer-events-auto"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              top: calPos.top ?? undefined,
              bottom: calPos.bottom ?? undefined,
              left: calPos.left ?? undefined,
              right: calPos.right ?? undefined,
            }}
          >
            <CalendarPopover
              rangeMode={false}
              stacked={calPos.stacked}
              firstNavRef={firstNavRef}
              startDate={value}
              endDate={null}
              onConfirm={(s) => {
                onChange(s);
                setOpen(false);
                setFocused(false);
              }}
              onCancel={() => {
                setOpen(false);
                setFocused(false);
              }}
              onClear={() => onChange(null)}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
